import type { AxiosInstance, AxiosError } from 'axios'
import { EGenaiErrors } from '@devglobalbot/global-contracts'
import type {
  IGenaiRateLimitDetails,
  IGenaiTreatTextDTO,
  IGenaiTreatTextResult,
} from '@devglobalbot/global-contracts'

/**
 * Erro normalizado das rotas /genai da API panel. A API responde erros com
 * status HTTP e envelope { success: false, error: { code, errorId, message } };
 * aqui o AxiosError vira um erro tipado com `code` estável (para mapear
 * mensagem localizada) e `errorId` (para o usuário informar ao suporte —
 * o erro completo fica rastreável nos logs do servidor por esse id).
 */
export interface GenaiErrorDetails extends Partial<IGenaiRateLimitDetails> {
  /** Campo recusado (`EGenaiNameField`), quando o código previr. */
  field?: string
  /** Caracteres recusados pela trava de caracteres. */
  invalidCharacters?: string[]
}

export class GenaiApiError extends Error {
  code: string
  errorId?: string
  serverMessage?: string
  details?: GenaiErrorDetails

  constructor (
    code: string,
    errorId?: string,
    serverMessage?: string,
    details?: GenaiErrorDetails
  ) {
    super(serverMessage || code)
    this.name = 'GenaiApiError'
    this.code = code
    this.errorId = errorId
    this.serverMessage = serverMessage
    this.details = details
  }
}

interface GenaiErrorEnvelope {
  success?: boolean
  error?: {
    code?: string
    errorId?: string
    message?: string
    details?: GenaiErrorDetails
  }
}

export function toGenaiError (e: unknown): GenaiApiError {
  if (e instanceof GenaiApiError) return e
  const axiosError = e as AxiosError<GenaiErrorEnvelope>
  const envelope = axiosError?.response?.data?.error
  if (envelope?.code) {
    return new GenaiApiError(
      envelope.code,
      envelope.errorId,
      envelope.message,
      envelope.details
    )
  }
  if (axiosError?.response) {
    return new GenaiApiError(EGenaiErrors.INTERNAL_ERROR)
  }
  // Sem resposta HTTP: queda de rede/CORS/timeout do próprio painel.
  return new GenaiApiError(EGenaiErrors.NETWORK_ERROR)
}

interface GenaiResultEnvelope<T> {
  success?: boolean
  result?: T
}

/** Origem da sessão do gateway: fluxo do bot ou testador do painel. */
export type TGenaiGatewayOrigin = 'flow' | 'playground'

/** As rotas /v2 respondem sucesso envelopado, diferente do router /genai. */
export interface GenaiGatewayEnvelope<T> {
  success: boolean
  result: T
}

export interface GenaiGatewaySession {
  sessionId: string
  assistantId: string
  provider: string
  hasKnowledgeBase: boolean
}

export interface GenaiGatewayAnswer {
  sessionId: string
  answer:
    | { type: 'text'; data: string }
    | { type: 'trigger'; data: { function_name: string } }
}

/**
 * Factory no padrão de kanbanSearchService: recebe a instância axios da API
 * panel (com o token de auth) e centraliza todas as chamadas /genai. Todo
 * método devolve o payload de sucesso cru e lança GenaiApiError em falha.
 */
export function createGenaiService (http: AxiosInstance) {
  async function request<T> (fn: () => Promise<{ data: T }>): Promise<T> {
    try {
      const { data } = await fn()
      return data
    } catch (e) {
      throw toGenaiError(e)
    }
  }

  /**
   * Desembrulha o envelope `{ success, result }` das rotas /v2 — as rotas
   * /genai legadas respondem o payload cru, e a diferença não deve vazar para
   * os componentes. Sucesso sem `result` é resposta malformada, não sucesso.
   */
  async function requestResult<T> (
    fn: () => Promise<{ data: GenaiResultEnvelope<T> }>
  ): Promise<T> {
    const data = await request(fn)
    if (!data?.success || !data.result) {
      throw new GenaiApiError(EGenaiErrors.INTERNAL_ERROR)
    }
    return data.result
  }

  return {
    getAssistant: (assistantId: string) =>
      request(() => http.get(`/genai?assistant_id=${assistantId}`)),

    listAssistants: () =>
      request(() => http.get('/genai/list')),

    createAssistant: (payload: object) =>
      request(() => http.post('/genai/create', payload)),

    updateAssistant: (assistantId: string, payload: object) =>
      request(() => http.put(`/genai/update/${assistantId}`, payload)),

    deleteAssistant: (assistantId: string) =>
      request(() => http.delete(`/genai/delete/${assistantId}`)),

    updateTrainingStatus: (assistantId: string) =>
      request(() => http.post(`/genai/update/training/status/${assistantId}`)),

    createWebsiteTraining: (assistantId: string, payload: object) =>
      request(() => http.post(`/genai/website/training/${assistantId}`, payload)),

    updateTraining: (assistantId: string, trainingId: string, payload: object) =>
      request(() => http.put(`/genai/update/training/${assistantId}/${trainingId}`, payload)),

    deleteTraining: (assistantId: string, fileId: string) =>
      request(() => http.delete(`/genai/training/delete/${assistantId}/${fileId}`)),

    createTrigger: (assistantId: string, payload: object) =>
      request(() => http.post(`/genai/trigger/${assistantId}`, payload)),

    updateTrigger: (assistantId: string, triggerId: string, payload: object) =>
      request(() => http.put(`/genai/update/trigger/${assistantId}/${triggerId}`, payload)),

    deleteTrigger: (assistantId: string, triggerId: string) =>
      request(() => http.delete(`/genai/trigger/delete/${assistantId}/${triggerId}`)),

    createSession: (assistantId: string) =>
      request(() => http.post(`/genai/session/${assistantId}`)),

    /**
     * Gateway de IA generativa (`/v2/genai/gateway`), que atende o assistente
     * OPENAI. Abre a conversa no provedor e devolve o `sessionId` local usado
     * nas perguntas seguintes.
     *
     * A conta vem do token, então a metadata só declara a origem: `playground`
     * é o testador do painel, que não tem bot, canal nem conversa.
     */
    createGatewaySession: (assistantId: string, origin: TGenaiGatewayOrigin) =>
      request<GenaiGatewayEnvelope<GenaiGatewaySession>>(() =>
        http.post(`/v2/genai/gateway/session/${assistantId}`, { metadata: { origin } })
      ).then(({ result }) => result),

    sendGatewayMessage: (sessionId: string, message: string) =>
      request<GenaiGatewayEnvelope<GenaiGatewayAnswer>>(() =>
        http.post(`/v2/genai/gateway/session/${sessionId}/message`, { message })
      ).then(({ result }) => result),

    getSession: (assistantId: string) =>
      request(() => http.get(`/genai/session/read?assistant_id=${assistantId}`)),

    deleteSession: (payload: object) =>
      request(() => http.put('/genai/session/delete', payload)),

    sendMessage: (sessionToken: string, payload: object) =>
      request(() => http.post(`/genai/message/${sessionToken}`, payload)),

    /**
     * Tratamento de texto sem sessão (assistente de escrita do chat ao vivo).
     * Debita 1 crédito genai como `write_assistant` e registra a resposta, do
     * mesmo modo que `sendMessage`.
     */
    treatText: (payload: IGenaiTreatTextDTO): Promise<IGenaiTreatTextResult> =>
      requestResult<IGenaiTreatTextResult>(() => http.post('/v2/genai/text', payload)),

    analyticsQuickview: (match: object) =>
      request(() => http.post('/genai/analytics/quickview', { match })),

    analyticsAnswered: (match: object) =>
      request(() => http.post('/genai/analytics/answered', { match })),

    analyticsList: (match: object, isCorrect?: unknown, pagination?: object) =>
      request(() => http.post('/genai/analytics/list', { match, isCorrect, pagination })),
  }
}

export type GenaiService = ReturnType<typeof createGenaiService>
